// src/hooks/useCurrentTenant.js
import { useState, useEffect } from "react";
import keycloak from "../auth/keycloak";
import { isEmbedded, getEmbeddedTenant } from "../auth/embeddedAuth"; // adjust path

const STORAGE_KEY = "tenant";
const TENANT_CHANGE_EVENT = "tenant-changed";

const clean = (t) => (t ? String(t).replace(/^\//, "") : "");

const readStored = () => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
};

// Store without firing the change event (safe to call during render)
const storeSilently = (tenant) => {
    try { localStorage.setItem(STORAGE_KEY, tenant); } catch {}
};

export function getCurrentTenant() {
    // 1. sent by the host via postMessage (embedded=1)
    if (isEmbedded()) {
        const t = clean(getEmbeddedTenant());
        if (t) return t;
    }

    // 2. ?tenantId=... in the URL (kept for later in-iframe navigation)
    const fromUrl = clean(new URLSearchParams(window.location.search).get("tenantId"));
    if (fromUrl) {
        if (readStored() !== fromUrl) storeSilently(fromUrl);
        return fromUrl;
    }

    // 3. stored choice (Home), if the user still belongs to it
    const groups = (keycloak.tokenParsed?.groups || []).map(clean);
    const stored = readStored();
    if (stored && (groups.length === 0 || groups.includes(stored))) return stored;

    // 4. default: first group from the token
    const first = groups[0] || "";
    if (first) storeSilently(first);
    return first;
}

export function setCurrentTenant(tenant) {
    const t = clean(tenant);
    storeSilently(t);
    // "storage" only fires in *other* tabs, so dispatch our own event
    // to notify listeners within this tab too.
    window.dispatchEvent(new CustomEvent(TENANT_CHANGE_EVENT, { detail: t }));
}

export function useCurrentTenant() {
    const [tenant, setTenant] = useState(getCurrentTenant);

    useEffect(() => {
        const handleChange = (e) => setTenant(e.detail ?? getCurrentTenant());
        const handleStorage = (e) => {
            if (e.key === STORAGE_KEY) setTenant(e.newValue || "");
        };

        window.addEventListener(TENANT_CHANGE_EVENT, handleChange);
        window.addEventListener("storage", handleStorage);

        return () => {
            window.removeEventListener(TENANT_CHANGE_EVENT, handleChange);
            window.removeEventListener("storage", handleStorage);
        };
    }, []);

    return tenant;
}